import { logger } from "../../lib/logger";

/**
 * Simplified response type used by all notification services.
 * Always has both `data` and `error` fields present.
 */
export type NotificationServiceResponse<T> = {
  data: T | null;
  error: { code: string; message: string; service?: string } | null;
};

/**
 * Base class for notification services
 * Provides common error handling, initialization, and utility methods
 *
 * All notification services should extend this class to ensure consistent
 * error handling and logging patterns.
 *
 * @example
 * ```typescript
 * class NotificationCRUD extends BaseNotificationService implements INotificationCRUD {
 *   readonly name = "notification-crud" as const;
 *   protected readonly serviceName = this.name;
 *
 *   async createNotification(data: CreateNotificationData) {
 *     return this.executeOperation("NOTIFICATION_CREATE", () =>
 *       typedSupabase.from("notifications").insert(data).select().single()
 *     );
 *   }
 * }
 * ```
 */
export abstract class BaseNotificationService {
  /**
   * Service name for logging and error reporting
   */
  protected abstract readonly serviceName: string;

  /**
   * Initialize service resources
   * Override in subclasses if needed
   */
  async initialize(): Promise<void> {
    // Base notification service requires no initialization
  }

  /**
   * Cleanup service resources
   * Override in subclasses if needed
   */
  async dispose(): Promise<void> {
    // Base notification service requires no cleanup
  }

  /**
   * Execute a database operation with standardized error handling.
   * Calls `validateInput` before execution and `logMetrics` after success.
   *
   * @param operationCode - Unique code for this operation (e.g., "NOTIFICATION_CREATE")
   * @param fn - Function that performs the database operation
   * @param metadata - Optional metadata passed to logMetrics on success
   * @returns NotificationServiceResponse with data or error
   */
  async executeOperation<T>(
    operationCode: string,
    fn: () => Promise<{ data?: T | null; error?: unknown }>,
    metadata?: Record<string, unknown>,
    input?: unknown
  ): Promise<NotificationServiceResponse<T>> {
    const startTime = performance.now();
    // Pre-execution hook: subclasses can override validateInput to enforce constraints
    this.validateInput(input);

    try {
      const { data, error } = await fn();

      if (error) {
        logger.error(`[${this.serviceName}] ${operationCode} error`, { error });
        const errObj = error as Record<string, unknown> | null;
        const errMessage = typeof errObj?.message === 'string' ? errObj.message : String(error);
        const errCode = typeof errObj?.code === 'string' ? errObj.code : `${operationCode}_ERROR`;
        return {
          data: null,
          error: {
            code: errCode,
            message: errMessage,
            service: this.serviceName,
          },
        };
      }

      this.logMetrics(operationCode, performance.now() - startTime, metadata || {});
      return { data: data ?? null, error: null };
    } catch (err) {
      logger.error(`[${this.serviceName}] ${operationCode} exception`, { error: err });
      return {
        data: null,
        error: {
          code: `${operationCode}_EXCEPTION`,
          message: err instanceof Error ? err.message : "Unknown error",
          service: this.serviceName,
        },
      };
    }
  }

  /**
   * Validate input data before database operations.
   * Override in subclasses to add specific validation logic.
   * Should throw an Error if validation fails.
   *
   * @param data - Data to validate
   */
  public validateInput(data: unknown): void {
    // Base implementation: no validation
  }

  /**
   * Log operation metrics for monitoring and debugging.
   * Override in subclasses to add monitoring/tracing integration.
   *
   * @param operationCode - Code for this operation
   * @param duration - Operation duration in milliseconds
   * @param metadata - Additional metadata to log
   */
  logMetrics(
    operationCode: string,
    duration: number,
    metadata: Record<string, unknown> = {}
  ): void {
    const threshold = typeof metadata.threshold === 'number' ? metadata.threshold : 1000;
    const message = `[${this.serviceName}] ${operationCode} metrics | ${Math.round(duration)}ms`;
    const context = { ...metadata, timestamp: new Date().toISOString() };
    if (duration > threshold) {
      logger.warn(message, context);
    } else {
      logger.info(message, context);
    }
  }
}
